import type { SelectCampaignPopoverProps } from './SelectCampaignPopover';

import { Box, BlockStack, Divider, InlineStack, Button, Scrollable } from '@shopify/polaris';
import { useResetDatepickerByCampaign } from '~/presentation/reports/ui/SelectDate';
import { PopoverStatus } from '~/presentation/reports/ui/SelectPopover';
import { useControlDataChange } from '~/presentation/reports/common';
import { CampaignSelector } from './CampaignSelector';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

export const CampaignChoice: React.FC<SelectCampaignPopoverProps> = ({
  defaultValue,
  id,
  isLoading,
  togglePopoverActive,
  campaignOptions,
  campaignGroupId,
}) => {
  const [tempSelectedItems, setTempSelectedItems] = useState<string[]>(
    (defaultValue as string[]) || [],
  );

  const { t } = useTranslation();
  const { emitControlChange } = useControlDataChange();
  const { resetDatepicker } = useResetDatepickerByCampaign();

  const handleChange = (value: string) => {
    const updatedOptions = tempSelectedItems.includes(value)
      ? tempSelectedItems.filter((item) => item !== value)
      : [...tempSelectedItems, value];

    setTempSelectedItems(updatedOptions);
  };

  const handleApplySelection = () => {
    emitControlChange({ [id]: tempSelectedItems });
    togglePopoverActive(false);
    resetDatepicker(tempSelectedItems, campaignGroupId);
  };

  const handleClearSelection = () => {
    setTempSelectedItems([]);
  };

  return (
    <BlockStack>
      <Scrollable vertical scrollbarWidth="thin" horizontal={false} className="max-h-[271px]">
        {!!campaignOptions?.length && (
          <Box padding="300">
            <BlockStack gap="200">
              {campaignOptions?.map((campaign) => (
                <CampaignSelector
                  onChange={handleChange}
                  key={campaign.value}
                  variants={campaign.variants}
                  id={campaign.value}
                  isSelected={tempSelectedItems.includes(campaign.value)}
                  isSelectMultiple
                />
              ))}
            </BlockStack>
          </Box>
        )}
      </Scrollable>
      <PopoverStatus isLoading={isLoading} isShowNoData={!campaignOptions?.length} />
      <Divider borderColor="border" />
      <Box paddingBlock="200" paddingInline="300">
        <InlineStack gap="300" align="end">
          <Button
            variant="plain"
            onClick={handleClearSelection}
            disabled={!tempSelectedItems.length || isLoading}
          >
            {t('Clear')}
          </Button>
          <Button
            variant="primary"
            onClick={handleApplySelection}
            disabled={!tempSelectedItems.length || isLoading}
          >
            {t('Apply')}
          </Button>
        </InlineStack>
      </Box>
    </BlockStack>
  );
};
